/**
 * Peragon review summaries -- the shared rating layer.
 *
 * One place that knows how to turn a rating into stars, how to fetch the
 * "4.9 out of 4,062 reviews" rollup for a reporting category, and how to paint a
 * badge, so any section / snippet / product line can show a rating without
 * another copy of the fetch, the star markup and the number formatting.
 *
 * Every rating on the site was previously hardcoded in Liquid -- three copies of
 * "4.9 out of 3686 reviews" plus a flat overall-star-rating.png that could not
 * reflect a real rating -- while the two places that had live numbers
 * (truck-fitment.js from /availability, peragon-reviews.js from /reviews) each
 * kept them to themselves.
 *
 * Consumers:
 *   snippets/reviews-summary-badge.liquid -- declarative badge, painted on load
 *   assets/peragon-reviews.js             -- review list + in-section summary
 *   assets/truck-fitment.js               -- publishes /availability rollups
 *
 * Public API (window.PeragonReviews):
 *   starMarkup(rating, className)  -> HTML for a 5-star row, fractional fill
 *   formatCount(value)             -> "4,062"
 *   formatRating(value)            -> "4.9"
 *   summaryText(rating, count)     -> "4.9 out of 4,062 reviews"
 *   load(category, fallbackUrl)    -> Promise<{rating, count} | null>
 *   publish(category, summary)     -> cache + repaint every matching badge
 *   paint(el, summary)             -> paint one badge element
 *   refresh(scope)                 -> paint every badge under scope
 *   subscribe(fn)                  -> called on every published summary
 *
 * `category` accepts a name ('landmark', 'tectonic', 'all') or raw
 * reporting_category_id(s) ('1', 4, '1,4'), so callers can pass whichever they
 * already have.
 */
(function () {
  'use strict';

  //Idempotent: the reviews section and the fitment snippet both declare this as a
  //dependency, so the same asset can legitimately be included twice on one page.
  if (window.PeragonReviews) return;

  var API = 'https://admin.peragon.com/shopify/reviews';
  var CACHE_KEY = 'peragon_review_summary_v1';
  var CACHE_TTL_MS = 6 * 60 * 60 * 1000;
  var TEXT_TEMPLATE = '{rating} out of {count} {line} reviews';

  //Mirrors products.reporting_category_id in admin. Names exist so callers can say
  //'landmark' rather than remembering that Landmark is 1 and Tectonic is 4.
  var NAMED = {
    landmark: '1',
    tectonic: '4',
    all: '1,4'
  };

  /**
   * Display name per reporting_category_id, for the {line} placeholder.
   *
   * The rating is a whole product line's, and the ERP has no finer grain: a
   * review records reporting_category_id and a product of "Landmark" or
   * "Tectonic", with no edition/variant/SKU field, and /reviews accepts no
   * filter but reporting_category_id. So a shopper switching Classic ->
   * Platinum will not see the count move, and naming the line is what makes
   * that correct rather than looking stale.
   *
   * If the admin ever tags reviews by edition, this is where a finer label
   * would come from -- along with a filter on the request.
   */
  var LINE_NAMES = {
    '1': 'Landmark',
    '4': 'Tectonic'
  };

  var summaries = {};  //category key -> { rating, count }
  var pending = {};    //category key -> in-flight promise; N badges cost one request
  var listeners = [];

  // ---------------------------------------------------------------------------
  // Formatting
  // ---------------------------------------------------------------------------

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatCount(value) {
    var number = Number(value);
    if (!isFinite(number)) return String(value || '0');
    return Math.round(number).toLocaleString('en-US');
  }

  //The API sends "4.9" / "5.0" as strings and the badge has always shown one
  //decimal, so 5 renders as "5.0" rather than a bare "5".
  function formatRating(value) {
    var number = Number(value);
    if (!isFinite(number)) return String(value == null ? '' : value);
    return number.toFixed(1);
  }

  //'' for a multi-category rollup, so a site-wide badge stays "4.9 out of 4,929
  //reviews" rather than naming a line it does not represent.
  function lineName(category) {
    return LINE_NAMES[normaliseCategory(category)] || '';
  }

  function renderTemplate(template, summary, category) {
    return String(template || TEXT_TEMPLATE)
      .replace(/\{rating\}/g, formatRating(summary.rating))
      .replace(/\{count\}/g, formatCount(summary.count))
      .replace(/\{line\}/g, lineName(category))
      //{line} resolves to '' for a rollup, which would otherwise leave a double
      //space mid-sentence.
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function summaryText(rating, count, category) {
    return renderTemplate(TEXT_TEMPLATE, { rating: rating, count: count }, category);
  }

  /**
   * Five stars with fractional fill on the partial one. `className` is an extra
   * modifier on the row (e.g. peragon-review-stars--compact) which is what sets
   * the star size, via --peragon-review-star-size in peragon-reviews-summary.css.
   */
  function starMarkup(rating, className) {
    var value = Number(rating);
    if (!isFinite(value)) value = 0;
    value = Math.max(0, Math.min(5, value));

    var label = value.toFixed(1).replace(/\.0$/, '') + ' out of 5 stars';
    var html = '<span class="peragon-review-stars ' + (className || '') +
      '" role="img" aria-label="' + escapeHtml(label) + '">';

    for (var i = 1; i <= 5; i++) {
      var fill = Math.max(0, Math.min(1, value - (i - 1)));
      var state = 'empty';
      var style = '';
      if (fill >= 0.95) {
        state = 'full';
      } else if (fill > 0.05) {
        state = 'partial';
        //Rounded because 3.4 lands on 39.99999999999999% otherwise.
        style = ' style="--fill:' + Math.round(fill * 1000) / 10 + '%"';
      }
      html += '<span class="peragon-review-star peragon-review-star--' + state + '"' + style + '></span>';
    }

    return html + '</span>';
  }

  // ---------------------------------------------------------------------------
  // Category keys
  // ---------------------------------------------------------------------------

  /**
   * One token -> reporting_category_id(s), or null if it means nothing.
   *
   * Template variants are matched by the product line inside their name, so
   * 'tectonic-2' and 'landmark-lp' resolve the way a reader would expect rather
   * than being passed through as a bogus reporting_category_id.
   */
  function resolveToken(token) {
    if (/^\d+$/.test(token)) return token;
    if (NAMED[token]) return NAMED[token];

    var name = Object.keys(NAMED).filter(function (key) {
      return key !== 'all' && token.indexOf(key) !== -1;
    })[0];
    return name ? NAMED[name] : null;
  }

  /**
   * Canonical, order-independent cache key for a category argument. Anything
   * unrecognised falls back to the site-wide total with a warning: a real rating
   * for the whole catalogue beats an empty badge, and the warning names the bad
   * value so the caller can be fixed.
   */
  function normaliseCategory(value) {
    var raw = String(value == null ? '' : value).trim().toLowerCase();
    if (!raw) return NAMED.all;

    //A token can expand to several ids ('all' -> '1,4'), so flatten, dedupe and
    //sort -- '4,1' and '1,4' must not become two cache entries.
    var ids = [];
    raw.split(',').forEach(function (token) {
      var resolved = resolveToken(token.trim());
      if (!resolved) return;
      resolved.split(',').forEach(function (id) {
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
    });

    if (!ids.length) {
      console.warn('[peragon-reviews] unknown review category "' + value +
        '", falling back to the site-wide total.');
      return NAMED.all;
    }

    return ids.sort().join(',');
  }

  //The endpoint scopes to one category or returns the combined rollup; there is no
  //multi-id filter, so anything but a single id is the site-wide total.
  function categoryParam(key) {
    var ids = key.split(',');
    return ids.length === 1 ? ids[0] : null;
  }

  //Keys in peragon-reviews-fallback.json: "1", "4", "all".
  function fallbackKey(key) {
    return categoryParam(key) || 'all';
  }

  // ---------------------------------------------------------------------------
  // sessionStorage cache -- so a repeat page view paints instantly instead of
  // flashing an empty badge while the request is in flight.
  // ---------------------------------------------------------------------------

  function readStore() {
    try {
      return JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || '{}') || {};
    } catch (err) {
      return {};
    }
  }

  function readCache(key) {
    var entry = readStore()[key];
    if (!entry || !isFinite(Number(entry.at))) return null;
    if (Date.now() - Number(entry.at) > CACHE_TTL_MS) return null;
    return toSummary(entry);
  }

  function writeCache(key, summary) {
    try {
      var store = readStore();
      store[key] = { rating: summary.rating, count: summary.count, at: Date.now() };
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(store));
    } catch (err) {
      //Private-mode Safari and storage-blocked browsers; caching is an optimisation.
    }
  }

  // ---------------------------------------------------------------------------
  // Fetching
  // ---------------------------------------------------------------------------

  /**
   * Accepts every shape the numbers arrive in -- the /reviews payload
   * (review_rating / review_count / total), a per-product row from
   * /availability (review_rating / review_count) and our own cache entries --
   * and returns null unless both values are real.
   */
  function toSummary(source) {
    if (!source) return null;
    var rating = Number(source.rating != null ? source.rating : source.review_rating);
    var count = Number(
      source.count != null ? source.count
        : (source.review_count != null ? source.review_count : source.total)
    );
    if (!isFinite(rating) || !isFinite(count) || count <= 0) return null;
    return { rating: rating, count: count };
  }

  //limit=1 because only the rollup fields are wanted; the review rows are the
  //review list's job.
  function requestLive(key) {
    var url = API + '?page=1&limit=1';
    var param = categoryParam(key);
    if (param) url += '&reporting_category_id=' + encodeURIComponent(param);

    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }).then(toSummary);
  }

  //admin.peragon.com only allows the live storefront origin, so theme previews on
  //*.shopifypreview.com fall back to the JSON snapshot shipped with the theme.
  function requestFallback(key, fallbackUrl) {
    if (!fallbackUrl) return Promise.resolve(null);
    return fetch(fallbackUrl).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }).then(function (data) {
      return toSummary(data && data[fallbackKey(key)]);
    }).catch(function () {
      return null;
    });
  }

  /**
   * Resolve one category's summary. Requests at most once per page per category;
   * later callers get the same promise, and a cached value is returned if the
   * request fails.
   */
  function load(category, fallbackUrl) {
    var key = normaliseCategory(category);
    if (pending[key]) return pending[key];
    if (summaries[key]) return Promise.resolve(summaries[key]);

    pending[key] = requestLive(key)
      .catch(function (err) {
        console.warn('[peragon-reviews] summary API unavailable, trying theme fallback.', err);
        return requestFallback(key, fallbackUrl);
      })
      .then(function (summary) {
        pending[key] = null;
        if (summary) return publish(key, summary);
        return summaries[key] || readCache(key) || null;
      });

    return pending[key];
  }

  // ---------------------------------------------------------------------------
  // Painting / publishing
  // ---------------------------------------------------------------------------

  function badges(scope) {
    return (scope || document).querySelectorAll('[data-reviews-summary-target]');
  }

  function badgeCategory(el) {
    return normaliseCategory(el.getAttribute('data-reviews-category'));
  }

  /**
   * Paint one badge. Slots are opt-in, so a caller can show stars only, text only
   * or both: [data-reviews-stars] takes the star row (its value is an extra
   * modifier class) and [data-reviews-text] takes the label (its value is an
   * optional template using {rating} / {count}).
   */
  function paint(el, summary) {
    summary = toSummary(summary);
    if (!el || !summary) return false;

    var key = badgeCategory(el);

    var starsEl = el.querySelector('[data-reviews-stars]');
    if (starsEl) {
      starsEl.innerHTML = starMarkup(summary.rating, starsEl.getAttribute('data-reviews-stars') || '');
    }

    var textEl = el.querySelector('[data-reviews-text]');
    if (textEl) {
      textEl.textContent = renderTemplate(textEl.getAttribute('data-reviews-text'), summary, key);
    }

    //The old markup carried alt="4.9 out of 5 Stars" as a hardcoded claim; the
    //accessible name now tracks the real numbers.
    var link = el.querySelector('a');
    if (link) {
      link.setAttribute('aria-label', summaryText(summary.rating, summary.count, key) + ', read the reviews');
    }

    el.removeAttribute('aria-busy');
    el.setAttribute('data-reviews-loaded', 'true');
    return true;
  }

  /**
   * Record a summary and repaint every badge for that category. Used by the fetch
   * path above and by anything that already has live numbers in hand --
   * truck-fitment.js gets them per product from /availability, peragon-reviews.js
   * gets them with every page of the list -- so no badge waits on its own request
   * when the answer is already on the page.
   */
  function publish(category, summary) {
    var key = normaliseCategory(category);
    var next = toSummary(summary);
    if (!next) return null;

    var previous = summaries[key];
    summaries[key] = next;
    writeCache(key, next);

    badges().forEach(function (el) {
      if (badgeCategory(el) === key) paint(el, next);
    });

    if (!previous || previous.rating !== next.rating || previous.count !== next.count) {
      listeners.forEach(function (fn) {
        try {
          fn({ category: key, rating: next.rating, count: next.count });
        } catch (err) {
          console.error('[peragon-reviews] summary listener failed', err);
        }
      });
      document.dispatchEvent(new CustomEvent('peragon:reviews-summary', {
        detail: { category: key, rating: next.rating, count: next.count }
      }));
    }

    return next;
  }

  /**
   * Paint and, where needed, fetch every badge under `scope`. Called on load for
   * the whole document; pass a container after injecting markup at runtime.
   */
  function refresh(scope) {
    badges(scope).forEach(function (el) {
      var key = badgeCategory(el);

      //Cached first so the badge is never blank on a repeat view, then the live
      //number overwrites it when it lands.
      var cached = summaries[key] || readCache(key);
      if (cached) {
        summaries[key] = cached;
        paint(el, cached);
      }

      load(key, el.getAttribute('data-reviews-fallback')).then(function (summary) {
        if (summary) {
          paint(el, summary);
          return;
        }
        //Nothing to show: either the category has no reviews yet (the ERP only
        //has them for Landmark and Tectonic -- accessories return count 0) or
        //both the API and the fallback failed. Either way the badge must not sit
        //there as a blank reserved gap, so collapse it.
        if (el.getAttribute('data-reviews-loaded') !== 'true') {
          el.removeAttribute('aria-busy');
          el.setAttribute('data-reviews-empty', 'true');
        }
      });
    });
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return function () {};
    listeners.push(fn);

    //Replay what is already known, so a late subscriber is not left waiting for a
    //change that may never come.
    Object.keys(summaries).forEach(function (key) {
      fn({ category: key, rating: summaries[key].rating, count: summaries[key].count });
    });

    return function () {
      listeners = listeners.filter(function (item) { return item !== fn; });
    };
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  window.PeragonReviews = {
    CATEGORIES: NAMED,
    escapeHtml: escapeHtml,
    starMarkup: starMarkup,
    formatCount: formatCount,
    formatRating: formatRating,
    summaryText: summaryText,
    lineName: lineName,
    normaliseCategory: normaliseCategory,
    load: load,
    publish: publish,
    paint: paint,
    refresh: refresh,
    subscribe: subscribe,
    get: function (category) { return summaries[normaliseCategory(category)] || null; }
  };

  ready(function () { refresh(); });
})();
